import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Chip, IconButton, MenuItem, Select } from "@mui/material";
import classNames from "classnames";
import dayjs from "dayjs";
import * as React from "react";

import { RobotoTypography } from "@/shared/components";
import { SearchField } from "@/shared/components/filters";
import { TriggerForEachPrimitive } from "@/shared/domain/actions/TriggerRecord";
import { ConditionsOperator } from "@/types";

import { TriggerCondition } from "./schema";
import { TriggerConditionInput } from "./TriggerConditionInput";
import styles from "./TriggerConditionsList.module.css";
import { parseFieldName } from "./utils";

interface TriggerConditionsListProps {
  conditions: TriggerCondition[];
  operator: ConditionsOperator;
  onOperatorChange: (operator: ConditionsOperator) => void;
  onDeleteCondition: (condition: TriggerCondition) => void;
  onEditCondition: (condition: TriggerCondition) => void;
  editable: boolean;
  // Props for inline editing
  conditionToEdit: TriggerCondition | null;
  showInput: boolean;
  searchFields: SearchField[];
  onCreate: (condition: TriggerCondition) => void;
  onUpdate: (condition: TriggerCondition) => void;
  onCancel: () => void;
  forEachPrimitive: TriggerForEachPrimitive;
  availableMetadataKeys?: string[];
}

function formatValue(condition: TriggerCondition): string {
  if (condition.searchFieldType === "date") {
    return dayjs(condition.value).format("YYYY-MM-DD");
  }
  return condition.value;
}

/**
 * Displays a list of trigger conditions with resource prefix badges.
 *
 * Features:
 * - Shows conditions as chips/badges
 * - Displays resource prefix badges (color-coded) based on condition.resourcePrefix
 * - Allows editing and deleting conditions
 * - Group operator selector (AND/OR)
 * - Inline editing: when a condition is being edited, the form appears in place of that condition
 *
 * Note: The resource prefix badge is always displayed using the condition's resourcePrefix
 * property, not parsed from the field name. This ensures backwards-compatible conditions
 * (with unprefixed field names) still show the correct "DATASET" badge.
 */
export const TriggerConditionsList: React.FC<TriggerConditionsListProps> = ({
  conditions,
  operator,
  onOperatorChange,
  onDeleteCondition,
  onEditCondition,
  editable,
  conditionToEdit,
  showInput,
  searchFields,
  onCreate,
  onUpdate,
  onCancel,
  forEachPrimitive,
  availableMetadataKeys = [],
}) => {
  if (conditions.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <RobotoTypography variant="body2" color="text.secondary">
          No conditions defined. Click &quot;Add Condition&quot; to create one.
        </RobotoTypography>
      </Box>
    );
  }

  return (
    <Box>
      {conditions.map((condition, index) => {
        // Check if this condition is being edited
        const isBeingEdited = showInput && conditionToEdit?.id === condition.id;

        // If this condition is being edited, render the edit form instead
        if (isBeingEdited) {
          return (
            <Box key={condition.id}>
              {/* Group Operator Selector (for 2nd+ conditions) */}
              {index > 0 && (
                <Box className={styles.operatorSelectWrapper}>
                  <Select
                    value={operator}
                    disabled={!editable}
                    onChange={(e) => {
                      onOperatorChange(e.target.value as ConditionsOperator);
                    }}
                    size="small"
                    className={styles.operatorSelect}
                    data-cy="group-operator-select"
                  >
                    <MenuItem value="AND">AND</MenuItem>
                    <MenuItem value="OR">OR</MenuItem>
                  </Select>
                </Box>
              )}
              <TriggerConditionInput
                searchFields={searchFields}
                conditionToEdit={conditionToEdit}
                onCreate={onCreate}
                onUpdate={onUpdate}
                onCancel={onCancel}
                forEachPrimitive={forEachPrimitive}
                availableMetadataKeys={availableMetadataKeys}
              />
            </Box>
          );
        }

        // Otherwise, render the normal condition item
        const { baseName } = parseFieldName(condition.field);
        const displayValue = formatValue(condition);

        return (
          <Box
            key={condition.id}
            className={classNames(
              styles.conditionItem,
              editable && styles.conditionItemEditable,
            )}
            onClick={() => editable && onEditCondition(condition)}
          >
            {/* Group Operator Display (for 2nd+ conditions) - Read-only */}
            {index > 0 && (
              <Box className={styles.operatorDisplay}>
                <RobotoTypography
                  variant="body2"
                  className={styles.operatorText}
                >
                  {operator}
                </RobotoTypography>
              </Box>
            )}

            {/* Resource Prefix Badge - Always displayed using condition.resourcePrefix */}
            <Chip
              label={condition.resourcePrefix.toUpperCase()}
              size="small"
              className={styles.prefixBadge}
              data-cy="resource-prefix-badge"
            />

            {/* Condition Details */}
            <Box className={styles.conditionDetails}>
              <RobotoTypography variant="body2">
                {baseName} {condition.comparator} {displayValue}
              </RobotoTypography>
            </Box>

            {/* Action Buttons */}
            {editable && (
              <Box className={styles.actionButtons}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditCondition(condition);
                  }}
                  data-cy="edit-condition-button"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteCondition(condition);
                  }}
                  data-cy="delete-condition-button"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
